import React, {useRef} from 'react';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {AssetUsageBadge} from './Badges/AssetUsageBadge';
import {AssetErrorBadge} from './Badges/AssetErrorBadge';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {
  HelpButton,
  Button,
  SearchIcon,
  Theme,
  ThemeName,
  TrashIcon,
} from '@taopix/taopix-design-system';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import useKeyboardMode from '../../../Hooks/useKeyboardMode';

export type ThumbnailOverlayDisplayMode = 'hover' | 'static';

export type ThumbnailOverlaysProps = {
  image: AssetMetaInterface;
  showPricing?: boolean;
  showLabel?: boolean;
  used?: number;
  localeString?: string;
  currencyCode?: string;
  expired?: boolean;
  openDetailsDialog?: Function;
  disabled?: boolean;
  showDeleteButton?: boolean;
  displayMode: ThumbnailOverlayDisplayMode;
  maxSelectionReached?: boolean;
  onDeleteAsset?: (id: number) => void;
};

const ThumbnailOverlays = ({
  image,
  showPricing,
  showLabel = false,
  used,
  localeString,
  currencyCode,
  expired,
  openDetailsDialog,
  disabled,
  showDeleteButton = false,
  displayMode = 'hover',
  maxSelectionReached = false,
  onDeleteAsset = (id: number) => {},
}: ThumbnailOverlaysProps) => {
  const {t} = useTranslation();
  const ui = useUiState();
  const keyboardMode = useKeyboardMode();
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  const priceWrapperClasses = classNames(
    'absolute flex flex-wrap justify-end right-xxs space-x-xs top-xxs items-start'
  );

  const showBadge: boolean = used && used > 0 ? true : false;

  const showPrice =
    showPricing && image?.link?.priceType && image?.link?.priceType > 0 ? true : false;

  let priceTypeStyle: ThemeName = ThemeName.Tip;

  if (showPrice) {
    // Set up the style for this price
    if (image?.link?.priceType === 1) {
      priceTypeStyle = ThemeName.PriceType1;
    } else if (image?.link?.priceType === 2) {
      priceTypeStyle = ThemeName.PriceType2;
    }
  }

  const assetLabels = (() => {
    if (showLabel && Array.isArray(image.label) && image.label.length > 0) {
      return image.label.map(label => {
        return (
          <Theme
            key={image.ref}
            name={ThemeName.Primary}
            element="p"
            className={'rounded-themeCornerSize px-xs py-xxs text-sm mt-xxs'}
          >
            {label}
          </Theme>
        );
      });
    }

    return null;
  })();

  const buttonContainerClasses = classNames(
    'absolute',
    'bottom-xxs',
    'right-xxs',
    'flex',
    'space-x-xs'
  );

  const buttonClasses = classNames(
    'cursor-pointer',
    'transition-opacity',
    'focus-visible:opacity-100',
    'hover' === displayMode && !keyboardMode ? 'mouse:opacity-0' : '', // Hide the zoom button by default on mouse-driven devices (except when navigating with a keyboard)
    'touch:opacity-1', // Show the zoom button on touch devices (this ensures it is shown on touch+mouse devices)
    'hover' === displayMode ? 'group-hover/thumbnail:mouse:opacity-100' : '' // Show the zoom button when hovering over with a mouse
  );

  return (
    <>
      {showPrice && (
        <div className={priceWrapperClasses}>
          <Theme
            name={priceTypeStyle}
            className={'px-xs rounded-full items-center'}
            allowCorners={false}
            key={`thumbnail-overlay-${image.ref}`}
          >
            <span className={'text-[11rem]'}>
              {image?.link?.unitSell
                ? Number(image?.link?.unitSell).toLocaleString(localeString, {
                    style: 'currency',
                    currency: currencyCode,
                  })
                : 0.0}
            </span>
          </Theme>
        </div>
      )}

      <div className={'absolute flex flex-wrap top-xs items-center right-xs min-h-[30rem]'}>
        {image.status === 8 && <AssetErrorBadge id={'error-' + image.id} />}
      </div>

      <div
        className={'absolute flex flex-wrap bottom-xxs items-center left-xxs min-h-[30rem] mr-xxl'}
      >
        {expired && (
          <HelpButton
            id={'expiredHelpButton' + image.id}
            toolTipLabel={t('flightcheck:str_ErrorPictureHasExpired')}
            placement={'right'}
            isSmallScreen={ui.state.isSmallScreen}
          />
        )}
        {showBadge && <AssetUsageBadge id={'usage-' + image.id} usage={used || 0} />}
        {disabled && !expired && !maxSelectionReached && (
          <HelpButton
            id={'disabledHelpButton' + image.id}
            toolTipLabel={t('helptips:str_MessagePictureAlreadyInProject')}
            isSmallScreen={ui.state.isSmallScreen}
          />
        )}
        {assetLabels}
      </div>
      {(showDeleteButton || openDetailsDialog) && (
        <div className={buttonContainerClasses}>
          {showDeleteButton && (
            <Button
              buttonStyle={'imageOverlay'}
              label={t('*:str_ButtonDelete')}
              size="small"
              hideLabel
              startIcon={<TrashIcon />}
              onClick={e => onDeleteAsset(image?.id as number)}
              disabled={(used || 0) > 0}
              className={classNames(
                buttonClasses,
                keyboardMode && (used || 0) > 0 ? 'opacity-50' : ''
              )}
            />
          )}

          {openDetailsDialog && (
            <Button
              buttonStyle={'imageOverlay'}
              label={t('*:str_ButtonViewDetails')}
              size="small"
              hideLabel
              startIcon={<SearchIcon />}
              onClick={() => openDetailsDialog(image, triggerButtonRef)}
              className={buttonClasses}
              aria-haspopup="dialog"
              ref={triggerButtonRef}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ThumbnailOverlays;
